import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BookOpen } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useLessonCompletions } from "@/hooks/useLessonCompletions";
import { useEffect, useState, memo, useRef } from "react";
import { Enrollment } from "@/types/enrollment";
import { devLog } from "@/utils/devLog";
import { getCoursePlayerUrl } from "@/utils/routePaths";
import { performance } from "@/utils/performance";

interface EnrolledCourseCardProps {
  enrollment: Enrollment;
}

// Memo the component to prevent unnecessary re-renders
const EnrolledCourseCard = memo(function EnrolledCourseCard({ enrollment }: EnrolledCourseCardProps) {
  const { completions, calculateProgress, totalLessons } = useLessonCompletions(enrollment.course_id);
  const [progress, setProgress] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  
  // Track initial render cost
  useEffect(() => {
    const mark = performance.start(`course-card-${enrollment.course_id.substring(0, 8)}`);
    performance.end(mark);
  }, [enrollment.course_id]);
  
  // Calculate progress percentage
  useEffect(() => {
    if (totalLessons > 0) {
      const progressPercentage = Math.round((completions.length / totalLessons) * 100);
      setProgress(progressPercentage);
    }
  }, [completions, totalLessons]);

  // Generate the correct course URL using our utility function
  const courseUrl = getCoursePlayerUrl(enrollment.course_id);
  
  // Check if the image is already in the browser cache
  useEffect(() => {
    if (enrollment.courses?.thumbnail_url) {
      // Check if image is already loaded in cache
      if (imageRef.current?.complete) {
        setImageLoaded(true);
        devLog("[EnrolledCourseCard] Image already in cache:", enrollment.courses?.title);
      } else {
        // Only preload if not in cache
        const img = new Image();
        img.onload = () => {
          setImageLoaded(true);
          devLog("[EnrolledCourseCard] Image loaded:", enrollment.courses?.title);
        };
        img.onerror = () => {
          setImageLoaded(true); // Show fallback
          devLog("[EnrolledCourseCard] Image failed to load:", enrollment.courses?.title);
        };
        img.src = enrollment.courses.thumbnail_url;
      }
    }
  }, [enrollment.courses?.thumbnail_url, enrollment.courses?.title]);
  
  // Handle case where courses might be undefined
  if (!enrollment.courses) {
    return (
      <div className="rounded-md overflow-hidden border bg-card p-4 text-center">
        <p className="text-muted-foreground">Course information unavailable</p>
      </div>
    );
  }

  return (
    <Link 
      key={enrollment.id} 
      to={courseUrl}
      className="block group"
    >
      <div className="rounded-md overflow-hidden border bg-card transition-all hover:shadow-md">
        <div className="aspect-video w-full relative">
          {!imageLoaded && enrollment.courses.thumbnail_url && (
            <div className="w-full h-full bg-muted absolute inset-0 flex items-center justify-center">
              <BookOpen className="h-10 w-10 text-muted-foreground animate-pulse" />
            </div>
          )}
          
          {enrollment.courses.thumbnail_url ? (
            <img 
              ref={imageRef}
              src={enrollment.courses.thumbnail_url} 
              alt={enrollment.courses.title}
              className={`w-full h-full object-cover transition-opacity duration-300 ${!imageLoaded ? 'opacity-0' : 'opacity-100'}`}
              loading="lazy"
              decoding="async"
              fetchPriority="high"
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <BookOpen className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
        </div>
        
        <div className="p-4">
          <h3 className="font-medium line-clamp-1 group-hover:text-primary transition-colors">
            {enrollment.courses.title}
          </h3>
          
          {/* Creator info */}
          {enrollment.courses.profiles && (
            <div className="flex items-center mt-1 text-sm text-muted-foreground">
              <Avatar className="h-5 w-5 mr-2">
                <AvatarImage 
                  src={enrollment.courses.profiles.avatar_url || undefined} 
                  alt={enrollment.courses.profiles.full_name || 'Creator avatar'}
                  loading="lazy"
                />
                <AvatarFallback>
                  {enrollment.courses.profiles.full_name?.charAt(0) || 
                   enrollment.courses.profiles.username?.charAt(0) || 'C'}
                </AvatarFallback>
              </Avatar>
              <span>
                {enrollment.courses.profiles.full_name || 
                 enrollment.courses.profiles.username || 'Creator'}
              </span>
            </div>
          )}
          
          {/* Progress indicator */}
          <div className="mt-3 space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{progress}% complete</span>
              <span>{completions.length}/{totalLessons} lessons</span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        </div>
      </div>
    </Link>
  );
});

export default EnrolledCourseCard;
